import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import './App.css';
import FileUpload from './components/DropAndUploadFile';
import ScriptList from './components/ScriptList';
import ScriptDetails from './components/ScriptDetails';
import AuthPage from './components/AuthPage';
import PipelinesPage from './components/PipelinesPage';
import { refreshToken, logout, getProfile } from './clients/grpc';
import { AuthContext, User } from './contexts/AuthContext';
import { getToken, setToken } from './contexts/tokenStore';
import { GetProfileRequest } from './generated/v1alpha1/auth_pb';

import { GoogleOAuthProvider } from '@react-oauth/google';

// import DarkModeToggle from './components/DarkModeToggle';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(getToken());
  const [user, setUser] = useState<User | null>(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const didAttemptRefresh = React.useRef(false);
  useEffect(() => {
    const attemptRefreshToken = async () => {
      if (isAuthenticated) {
        try {
          const response = await refreshToken();
          const newAccessToken = response.getAccessToken();
          if (newAccessToken) {
            setToken(newAccessToken);
            setAccessToken(newAccessToken);
            
            // Fetch profile after refresh
            try {
              const profileRes = await getProfile(new GetProfileRequest());
              const userPb = profileRes.getUser();
              if (userPb) {
                const userData: User = {
                  id: userPb.getId(),
                  email: userPb.getEmail(),
                  firstName: userPb.getFirstName(),
                  lastName: userPb.getLastName(),
                  role: userPb.getRole(),
                };
                setUser(userData);
              }
            } catch (profileError) {
              console.error('Failed to fetch profile', profileError);
            }

            setIsAuthenticated(true);
          } else {
            handleLogout();
          }
        } catch (error) {
          console.error('Refresh token failed', error);
          handleLogout();
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
      if (didAttemptRefresh.current) return;
      didAttemptRefresh.current = true;
      attemptRefreshToken();
  }, []);

  const handleLogin = (token: string, userData?: User) => {
    setAccessToken(token);
    setToken(token);
    if (userData) {
      setUser(userData);
    }
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed', error);
    }
    setAccessToken(null);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!googleClientId) {
    return <div>Loading... Google Client ID is missing</div>;
  }

  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    return user.email;
  };

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthContext.Provider value={{ accessToken, setAccessToken, user, setUser }}>
        <Router>
          <>
            {isAuthenticated && (
              <header className="header">
                <div className="header-container">
                  <div className="logo-container">
                    <img src={`${process.env.PUBLIC_URL}/squaresircle.png`} alt="SquareSircle Logo" className="logo-image" />
                    <h1 className="logo-text">SquareSircle</h1>
                  </div>
                  <nav className="nav-links">
                    <Link to="/" className="nav-link">Home</Link>
                    <Link to="/scripts" className="nav-link">Scripts</Link>
                    <Link to="/pipelines" className="nav-link">Pipelines</Link>
                    <span className="user-name">{getUserDisplayName()}</span>
                    <button onClick={handleLogout} className="logout-button">Logout</button>
                  </nav>
                  {/*<DarkModeToggle />*/}
                </div>
              </header>
            )}
            <main className={isAuthenticated ? "main-content-with-sidebar" : "main-content-auth"}>
              <Routes>
                <Route path="/auth" element={<AuthPage onLogin={handleLogin} />} />
                <Route
                  path="/"
                  element={isAuthenticated ? <Home /> : <Navigate to="/auth" />}
                />
                <Route
                  path="/scripts"
                  element={isAuthenticated ? <ScriptList /> : <Navigate to="/auth" />}
                />
                <Route
                  path="/scripts/:scriptName"
                  element={isAuthenticated ? <ScriptDetails /> : <Navigate to="/auth" />}
                />
                <Route
                  path="/pipelines"
                  element={isAuthenticated ? <PipelinesPage /> : <Navigate to="/auth" />}
                />
                <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/auth"} />} />
              </Routes>
            </main>
          </>
        </Router>
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};

const Home: React.FC = () => (
  <div className="home-layout">
    <div className="home-left">
      <div className="home-header">
        <h1 className="title">Automated Script Coverage</h1>
        <p className="subtitle">
          Upload your movie script PDFs and get detailed coverage automatically.
        </p>
      </div>
      
      <div className="features-simple">
        <div className="feature-item">
          <span className="feature-icon">🎯</span>
          <span>Best-in-class analysis models</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">⚡</span>
          <span>Fast processing</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">📊</span>
          <span>Detailed coverage reports</span>
        </div>
      </div>
    </div>
    
    <div className="home-right">
      <FileUpload />
    </div>
  </div>
);

export default App;
